import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import Select from 'react-select';
import { CURRENCY_SYMBOLS, CURRENCY_SYMBOLS_TEXT } from '../../utils/currencySymbols';

const SoldItemModal = ({ isOpen, onClose, onSubmit, item, currencies = ['USD'] }) => {
  const primaryCurrency = currencies[0] || 'USD';

  const [formData, setFormData] = useState({
    quantitySold: 1,
    soldPrice: '',
    soldPriceCurrency: primaryCurrency,
    notes: ''
  });

  // Create currency options from user's selected currencies
  const currencyOptions = currencies.map(curr => ({
    value: curr,
    label: CURRENCY_SYMBOLS_TEXT[curr]
  }));

  useEffect(() => {
    if (item && isOpen) {
      setFormData({
        quantitySold: 1,
        soldPrice: item.price ? Number(item.price).toFixed(2) : '',
        soldPriceCurrency: item.priceCurrency || primaryCurrency,
        notes: ''
      });
    }
  }, [item, isOpen, primaryCurrency]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const saleData = {
      itemId: item.id,
      itemName: item.name,
      quantitySold: parseInt(formData.quantitySold),
      soldPrice: parseFloat(formData.soldPrice),
      soldPriceCurrency: formData.soldPriceCurrency,
      notes: formData.notes,
      soldAt: new Date()
    };

    onSubmit(saleData);
    
    // Reset form
    setFormData({
      quantitySold: 1,
      soldPrice: item.price ? Number(item.price).toFixed(2) : '',
      soldPriceCurrency: item.priceCurrency || primaryCurrency,
      notes: ''
    });
  };

  if (!isOpen || !item) return null;

  const maxQuantity = item.quantity || 0;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 transition-opacity bg-black bg-opacity-50 backdrop-blur-sm z-[60]"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-xl text-left 
              overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle 
              sm:max-w-lg sm:w-full relative z-[70]"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Record Sale
                </h3>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <FiX className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                </button>
              </div>

              {/* Item Info */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                  {item.name}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Available Stock: <span className="font-semibold">{maxQuantity}</span>
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Quantity Sold *
                  </label>
                  <input
                    type="number"
                    placeholder="1"
                    min="1"
                    max={maxQuantity}
                    value={formData.quantitySold}
                    onChange={(e) => setFormData({ ...formData, quantitySold: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                      dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-teal-500 
                      focus:border-transparent transition-colors"
                    required
                  />
                  {formData.quantitySold > maxQuantity && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      Quantity sold cannot exceed available stock ({maxQuantity})
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sold Price *
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-1">
                      <Select
                        options={currencyOptions}
                        value={currencyOptions.find(opt => opt.value === formData.soldPriceCurrency)}
                        onChange={(selected) => setFormData({ ...formData, soldPriceCurrency: selected.value })}
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            backgroundColor: 'var(--select-bg)',
                            borderColor: state.isFocused ? '#14b8a6' : 'var(--select-border)',
                            boxShadow: state.isFocused ? '0 0 0 2px rgba(20, 184, 166, 0.2)' : 'none',
                            '&:hover': {
                              borderColor: '#14b8a6'
                            },
                            minHeight: '42px'
                          }),
                          menu: (base) => ({
                            ...base,
                            backgroundColor: 'var(--select-menu-bg)',
                            zIndex: 100
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isFocused ? 'var(--select-option-hover)' : 'transparent',
                            color: 'var(--select-text)',
                            cursor: 'pointer'
                          }),
                          singleValue: (base) => ({
                            ...base,
                            color: 'var(--select-text)'
                          })
                        }}
                        isSearchable={false}
                      />
                    </div>
                    <div className="col-span-2">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                          {CURRENCY_SYMBOLS[formData.soldPriceCurrency]}
                        </span>
                        <input
                          type="number"
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          value={formData.soldPrice}
                          onChange={(e) => setFormData({ ...formData, soldPrice: e.target.value })}
                          onBlur={(e) => {
                            // Format to 2 decimal places when leaving the input
                            if (e.target.value && !isNaN(e.target.value)) {
                              setFormData({
                                ...formData,
                                soldPrice: Number(e.target.value).toFixed(2)
                              });
                            }
                          }}
                          className="w-full pl-8 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                            dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-teal-500 
                            focus:border-transparent transition-colors"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    placeholder="e.g., Sold to repeat customer, offered discount..."
                    rows="3"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                      dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-teal-500 
                      focus:border-transparent transition-colors resize-none"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 
                      dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formData.quantitySold > maxQuantity || formData.quantitySold < 1}
                    className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-green-600 
                      text-white rounded-lg hover:shadow-lg transition-all font-medium
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Record Sale
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default SoldItemModal;

